import providerScopeConfig from "../../config/provider-scope.json";
import { MarketDataError, type MarketDataErrorReason } from "./errors";
import type { MarketProvider } from "./types";

export type ProviderCapabilityStatus =
  | "ENABLED"
  | "UNAVAILABLE"
  | "UNSUPPORTED"
  | "DISABLED_PENDING_WRITTEN_AUTHORIZATION";

export interface ProviderCapability {
  provider: MarketProvider;
  status: ProviderCapabilityStatus;
  productionMarketDataAllowed: boolean;
  disabledReason: MarketDataErrorReason;
}

interface RawProviderCapability {
  status: ProviderCapabilityStatus;
  productionMarketDataAllowed: boolean;
  disabledReason?: MarketDataErrorReason;
}

interface RawProviderScopeConfig {
  providers: Partial<Record<MarketProvider, RawProviderCapability>>;
}

const VALID_STATUSES = new Set<ProviderCapabilityStatus>([
  "ENABLED",
  "UNAVAILABLE",
  "UNSUPPORTED",
  "DISABLED_PENDING_WRITTEN_AUTHORIZATION",
]);

const VALID_DISABLED_REASONS = new Set<MarketDataErrorReason>([null, "PENDING_WRITTEN_AUTHORIZATION"]);

function assertRawCapability(provider: MarketProvider, value: unknown): RawProviderCapability {
  if (!value || typeof value !== "object") {
    throw new Error(`provider scope for ${provider} must be an object`);
  }
  const record = value as Record<string, unknown>;
  if (!VALID_STATUSES.has(record.status as ProviderCapabilityStatus)) {
    throw new Error(`provider scope for ${provider} has an invalid status`);
  }
  if (typeof record.productionMarketDataAllowed !== "boolean") {
    throw new Error(`provider scope for ${provider} must declare productionMarketDataAllowed`);
  }
  const disabledReason = (record.disabledReason ?? null) as MarketDataErrorReason;
  if (!VALID_DISABLED_REASONS.has(disabledReason)) {
    throw new Error(`provider scope for ${provider} has an invalid disabledReason`);
  }
  return {
    status: record.status as ProviderCapabilityStatus,
    productionMarketDataAllowed: record.productionMarketDataAllowed,
    disabledReason,
  };
}

function loadCapabilities(config: RawProviderScopeConfig): Record<MarketProvider, ProviderCapability> {
  return {
    polymarket: materializeCapability("polymarket", config.providers.polymarket),
    kalshi: materializeCapability("kalshi", config.providers.kalshi),
  };
}

function materializeCapability(
  provider: MarketProvider,
  raw: RawProviderCapability | undefined,
): ProviderCapability {
  if (!raw) {
    throw new Error(`provider scope is missing ${provider}`);
  }
  const capability = assertRawCapability(provider, raw);
  return {
    provider,
    status: capability.status,
    productionMarketDataAllowed: capability.productionMarketDataAllowed,
    disabledReason: capability.disabledReason ?? null,
  };
}

const CAPABILITIES = loadCapabilities(providerScopeConfig as RawProviderScopeConfig);

export function listProviderCapabilities(): ProviderCapability[] {
  return Object.values(CAPABILITIES);
}

export function getProviderCapability(provider: MarketProvider): ProviderCapability {
  return CAPABILITIES[provider];
}

function disabledMessage(provider: MarketProvider, reason: MarketDataErrorReason): string {
  if (reason === "PENDING_WRITTEN_AUTHORIZATION") {
    return `${providerLabel(provider)} market data is disabled pending written authorization`;
  }
  return `${providerLabel(provider)} market data is disabled`;
}

function providerLabel(provider: MarketProvider): string {
  return provider === "kalshi" ? "Kalshi" : "Polymarket";
}

export function assertProductionMarketDataEnabled(provider: MarketProvider): ProviderCapability {
  const capability = getProviderCapability(provider);
  if (capability.productionMarketDataAllowed) return capability;
  throw new MarketDataError("PROVIDER_DISABLED", disabledMessage(provider, capability.disabledReason), {
    reason: capability.disabledReason,
  });
}
